import { invenioConfig } from "@config";
import { getUiSchemaExtensions } from "@forms/rjsf/RJSFExtensionFields";
import _merge from "lodash/merge";

const arrayUiOptions = {
  "ui:options": {
    orderable: false,
  },
};

const identifierUiSchema = (schemeVocabulary) => ({
  ...arrayUiOptions,
  items: {
    scheme: {
      "ui:widget": "vocabulary",
      "ui:options": {
        vocabularyType: schemeVocabulary,
      },
    },
    "custom:grid": [
      {
        scheme: 6,
        value: 10,
      },
    ],
  },
});

const identifierWithMaterialUiSchema = (schemeVocabulary) => {
  const identifier = identifierUiSchema(schemeVocabulary);
  identifier["items"]["material"] = {
    "ui:widget": "vocabulary",
    "ui:options": {
      vocabularyType:
        invenioConfig.VOCABULARIES.document.doc_identifiers_materials,
    },
  };
  identifier["items"]["custom:grid"] = [
    {
      material: 5,
      scheme: 5,
      value: 5,
    },
  ];
  return identifier;
};

export const uiSchema = (title, colleges = [], programsByCollege = {}) => {
  const { uiSchemaExtensions, uiSchemaExtensionsGrid } = getUiSchemaExtensions(
    invenioConfig.SPECIAL_COLLECTIONS?.extensions || {}
  );

  const collegeEnumNames = colleges.map((college) => college.name);

  const _uiSchema = {
    ...uiSchemaExtensions,
    abstract: {
      "ui:widget": "textarea",
    },
    alternative_titles: {
      ...arrayUiOptions,
      items: {
        language: {
          "ui:widget": "vocabularySearch",
          "ui:placeholder": "Type a language...",
          "ui:options": {
            vocabularyType: "language",
          },
        },
        type: {
          "ui:widget": "vocabulary",
          "ui:options": {
            vocabularyType:
              invenioConfig.VOCABULARIES.document.alternativeTitle.type,
          },
        },
        "custom:grid": [
          {
            value: 16,
          },
          {
            language: 10,
            type: 6,
          },
        ],
      },
    },
    authors: {
      ...arrayUiOptions,
      items: {
        "custom:grid": [
          {
            full_name: 16,
          },
        ],
      },
    },
    advisers: {
      ...arrayUiOptions,
      items: {
        "custom:grid": [
          {
            full_name: 16,
          },
        ],
      },
    },
    academic_info: {
      department: {
        "ui:widget": "select",
        "ui:options": {
          enumNames: collegeEnumNames,
        },
      },
      program: {
        "ui:widget": "dependentVocabulary",
        "ui:options": {
          programsByCollege: programsByCollege,
          dependsOn: "academic_info.department",
        },
      },
      "custom:grid": [
        {
          campus: 4,
          department: 6,
          program: 6,
        },
      ],
    },

    call_number: {
      "ui:widget": "text",
      "ui:placeholder": "e.g., BEED 1 2020",
    },
    sections: {
      "ui:widget": "vocabulary",
      "ui:options": {
        vocabularyType: invenioConfig.VOCABULARIES.document.sections,
        useValue: true,
        defaultValue: "THESIS AND DISSERTATIONS",
      },
    },
    conference_info: {
      ...arrayUiOptions,
      items: {
        "custom:grid": [
          {
            title: 16,
          },
          {
            place: 8,
            dates: 8,
          },
          {
            acronym: 8,
            year: 8,
          },
        ],
      },
    },
    copyrights: {
      ...arrayUiOptions,
      items: {
        statement: {
          "ui:widget": "textarea",
        },
        "custom:grid": [
          {
            holder: 8,
            year: 4,
            statement: 4,
          },
        ],
      },
    },
    identifiers: identifierWithMaterialUiSchema(
      invenioConfig.VOCABULARIES.document.identifier.scheme
    ),
    imprint: {
      "custom:grid": [{ publisher: 8, date: 8 }],
    },
    internal_notes: {
      ...arrayUiOptions,
      items: {
        value: {
          "ui:widget": "textarea",
        },
        "custom:grid": [{ value: 16 }, { field: 8, user: 8 }],
      },
    },
    keywords: {
      ...arrayUiOptions,
      items: {
        "custom:grid": [{ value: 8, source: 8 }],
      },
    },
    languages: {
      ...arrayUiOptions,
      items: {
        "ui:widget": "vocabularySearch",
        "ui:placeholder": "Type a language...",
        "ui:options": {
          vocabularyType: "language",
        },
      },
    },
    licenses: {
      ...arrayUiOptions,
      items: {
        license: {
          id: {
            "ui:widget": "vocabularySearch",
            "ui:placeholder": "Type a license...",
            "ui:options": {
              vocabularyType: "license",
            },
          },
          "custom:grid": [{ id: 16 }],
        },
        "custom:grid": [{ license: 16 }, { material: 16 }],
      },
    },
    notes: {
      "ui:widget": "textarea",
    },
    publication_info: {
      ...arrayUiOptions,
      items: {
        note: {
          "ui:widget": "textarea",
        },
        "custom:grid": [
          { journal_title: 6, journal_volume: 5, journal_issue: 5 },
          { pages: 6, year: 5, note: 5 },
        ],
      },
    },
    subjects: {
      ...arrayUiOptions,
      items: {
        scheme: {
          "ui:widget": "vocabulary",
          "ui:options": {
            vocabularyType: invenioConfig.VOCABULARIES.document.doc_subjects,
          },
        },
        "custom:grid": [{ scheme: 6, value: 10 }],
      },
    },
    urls: {
      ...arrayUiOptions,
      items: {
        "custom:grid": [{ value: 10, description: 6 }],
      },
    },
    "custom:grid": [
      {
        title: 10,
        collection_type: 6,
      },
      {
        alternative_titles: 8,
        languages: 8,
      },
      {
        edition: 4,
        publication_year: 4,
        number_of_pages: 4,
        source: 4,
      },
      { "custom:divider": 16 },
      { authors: 8, advisers: 8 },
      { subjects: 8, keywords: 8 },
      { "custom:divider": 16 },
      { academic_info: 16 },
      { "custom:divider": 16 },
      { imprint: 16 },
      { identifiers: 16 },
      { publication_info: 8, conference_info: 8 },
      { "custom:divider": 16 },
      { abstract: 16 },
      { "custom:divider": 16 },
      { sections: 8, call_number: 8 },
      { copyrights: 8, licenses: 8 },
      { "custom:divider": 16 },
      { urls: 16 },
      { "custom:divider": 16 },
      { internal_notes: 16 },
      { notes: 16 },
      ...uiSchemaExtensionsGrid,
    ],
    "custom:root": {
      "custom:formTitle": title,
    },
  };

  return _merge(
    _uiSchema,
    invenioConfig.SPECIAL_COLLECTIONS?.editorUiSchema || {}
  );
};
